import * as React from 'react'
import styled, { css } from "styled-components"
import { Style } from '../styles'
import { useSelector } from 'react-redux'
import { AppState } from '../store'

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0px;
  left: 0px;
  background: ${props => props.theme.color.background};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  backface-visibility: hidden;
  overflow: hidden;
`

const Container = styled.div<{ open: boolean, notification: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  z-index: 2147483000;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  color-scheme: none;
  background: none;
  filter: drop-shadow(rgba(0, 0, 0, 0.06) 0px 1px 6px) drop-shadow(rgba(0, 0, 0, 0.16) 0px 2px 32px);
  position: fixed;
  bottom: 20px;
  right: 20px;
  transition: transform 167ms cubic-bezier(0.33, 0, 0, 1);
  transform-origin: center center;
  user-select: none;
  -webkit-tap-highlight-color:transparent;

  &:hover {
    transition: transform 250ms cubic-bezier(0.33, 0, 0, 1);
    transform: scale(1.1);
  }

  ${props => props.notification && css`
    ${Wrapper} {
      mask-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik00Mi45OTkxIDBDNDAuNTcwNyAxLjgyNDQ2IDM5IDQuNzI4OCAzOSA4QzM5IDEzLjE4NTMgNDIuOTQ2NyAxNy40NDg5IDQ4IDE3Ljk1MDZWNDhIMFYwSDQyLjk5OTFaIiBmaWxsPSJjdXJyZW50Q29sb3IiLz4KPC9zdmc+Cg==");
    }
  `}

  ${props => props.open && css`
    ${Wrapper} {
      mask-image: none;
    }

    ${NotificationBadge} {
      display: none;
    }

    @media screen and (max-width: ${Style.breakpoints.MOBILE}) {
      display: none;
    }
  `}
`

const OpenIcon = styled.div<{ open: boolean }>`
  position: absolute;
  fill: white;
  display: flex;
  justify-content: center;
  transition: transform 0.16s linear, opacity 0.08s linear;
  opacity: 1;
  transform: rotate(0deg) scale(1);
  fill: ${({ theme }) => theme.color.backgroundText};
  color: ${({ theme }) => theme.color.backgroundText};

  ${props => !props.open && css`
    opacity: 0;
    transform: rotate(30deg) scale(0);
  `}

  svg {
    width: 24px;
    height: 24px;
  }
`

const MinimizeIcon = styled.div<{ open: boolean }>`
  display: flex;
  justify-content: center;
  position: absolute;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  transition: transform 0.16s linear, opacity 0.08s linear;
  opacity: 0;
  transform: rotate(-60deg);
  fill: ${({ theme }) => theme.color.backgroundText};
  color: ${({ theme }) => theme.color.backgroundText};

  ${props => props.open && css`
    opacity: 1;
    transform: rotate(0deg);
  `}
`

const NotificationBadge = styled.div`
  z-index: 2147483001;
  position: absolute;
  border-radius: 50%;
  box-shadow: none;
  bottom: 32px;
  left: 41px;
  background: rgb(223, 32, 32);
  display: block;
  min-width: 16px;
  height: 16px;
  border-radius: 50%;
`

const NotificationBadgeIndicator = styled.span`
  background: rgb(223, 32, 32);
  display: flex;
  justify-content: center;
  font-size: 11px;
  color: rgb(250, 250, 250);
  font-weight: 700;
  border-radius: 9px;
  width: fit-content;
  min-width: 16px;
  height: 16px;
  box-sizing: border-box;
  line-height: 16px;
  padding: 0px 4px;
`

interface IProps {
  open: boolean
  onClick: () => void
}

const Launcher = (props: IProps) => {
  const { open, onClick } = props
  const counters = useSelector((state: AppState) => state.application.counters)
  const notificationsCount = counters && Object.values(counters).reduce((total, count) => {
    return total + count;
  }, 0);

  const hasNotification = notificationsCount > 0;

  return (
    <Container onClick={onClick} open={open} notification={hasNotification} className='launcher-frame'>
      <Wrapper>
        <OpenIcon open={!open}>
          <svg focusable="false" aria-hidden="true" viewBox="0 0 28 32" width="28" height="32"><path d="M28,32 C28,32 23.2863266,30.1450667 19.4727818,28.6592 L3.43749107,28.6592 C1.53921989,28.6592 0,27.0272 0,25.0144 L0,3.6448 C0,1.632 1.53921989,0 3.43749107,0 L24.5615088,0 C26.45978,0 27.9989999,1.632 27.9989999,3.6448 L27.9989999,22.0490667 L28,22.0490667 L28,32 Z M23.8614088,20.0181333 C23.5309223,19.6105242 22.9540812,19.5633836 22.5692242,19.9125333 C22.5392199,19.9392 19.5537934,22.5941333 13.9989999,22.5941333 C8.51321617,22.5941333 5.48178311,19.9584 5.4277754,19.9104 C5.04295119,19.5629428 4.46760991,19.6105095 4.13759108,20.0170667 C3.97913051,20.2124916 3.9004494,20.4673395 3.91904357,20.7249415 C3.93763774,20.9825435 4.05196575,21.2215447 4.23660523,21.3888 C4.37862552,21.5168 7.77411059,24.5386667 13.9989999,24.5386667 C20.2248893,24.5386667 23.6203743,21.5168 23.7623946,21.3888 C23.9467342,21.2215726 24.0608642,20.9827905 24.0794539,20.7254507 C24.0980436,20.4681109 24.0195551,20.2135019 23.8614088,20.0181333 Z"></path></svg>
        </OpenIcon>

        <MinimizeIcon open={open}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M18.601 8.39897C18.269 8.06702 17.7309 8.06702 17.3989 8.39897L12 13.7979L6.60099 8.39897C6.26904 8.06702 5.73086 8.06702 5.39891 8.39897C5.06696 8.73091 5.06696 9.2691 5.39891 9.60105L11.3989 15.601C11.7309 15.933 12.269 15.933 12.601 15.601L18.601 9.60105C18.9329 9.2691 18.9329 8.73091 18.601 8.39897Z" fill="currentColor"></path></svg>
        </MinimizeIcon>
      </Wrapper>

      {hasNotification && <NotificationBadge>
        <NotificationBadgeIndicator>
          {notificationsCount}
        </NotificationBadgeIndicator>
      </NotificationBadge>}
    </Container>
  )
}

export default Launcher